import React, { useState } from 'react';

import { useConditionalEffect } from 'src/commons';
import store, { actions, useGroupName } from 'src/data/redux';
import { SearchActivity, SearchActivityResult } from 'src/lib/hammerstoneApi';
import PAGES from 'src/nav/Pages';

import { FormField, Header, Modal, SpaceBetween, Table, TextFilter } from '@amzn/awsui-components-console';
import InternalLink from '../helpers/InternalLink';

const SEARCH_STRING_PATTERN = /^(?=.*[a-zA-Z0-9])[\w\-]{3,128}$/;

export default function SearchActivities() {
	const groupName = useGroupName();
	const [visible, setVisible] = useState(false);
	const [errorText, setErrorText] = useState('');
	const [loading, setLoading] = useState(false);
	const [searchString, setSearchString] = useState('');
	const [searchResults, setSearchResults] = useState<SearchActivityResult[]>([]);

	useConditionalEffect(() => {
		// When the modal is closed, reset the search string and the validation message
		setSearchString('');
		setErrorText('');
	}, [!visible]);

	/** This component is reused within the modal to allow customers to search again from the results page */
	const searchBar = (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				if (!groupName) {
					setErrorText('Select a group above to search activities');
				} else if (!SEARCH_STRING_PATTERN.test(searchString)) {
					setErrorText(
						'Search string must be 3-128 alphanumeric characters, hyphens, or underscores, with at least one alphanumeric character'
					);
				} else {
					setErrorText('');
					setVisible(true);
					setLoading(true);
					SearchActivity({ groupName, searchString })
						.then((output) => {
							setSearchResults(output?.results ?? []);
						})
						.catch((error) => {
							store.dispatch(
								actions.page.addToFlashbar({
									id: `search_activities_failure`,
									message: {
										type: 'error',
										header: `Failed to search group ${groupName} for activities with names like ${searchString}`,
										content: error.message || 'Unknown error',
										dismissible: true,
									},
								})
							);
						})
						.finally(() => setLoading(false));
				}
			}}
		>
			<SpaceBetween size="xs" direction="horizontal">
				<FormField errorText={errorText}>
					<TextFilter
						filteringText={searchString}
						onChange={({ detail }) => setSearchString(detail.filteringText)}
						filteringPlaceholder="Search activities by name..."
					/>
				</FormField>
			</SpaceBetween>
		</form>
	);

	return (
		<>
			{searchBar}
			<Modal
				size="xx-large"
				visible={visible}
				onDismiss={() => {
					setVisible(false);
				}}
				header={`Search activities in group ${groupName}`}
			>
				<Table
					header={<Header actions={searchBar}>Matching activities</Header>}
					columnDefinitions={[
						{
							id: 'activity',
							header: 'Activity',
							cell: ({ activityId, activityName }) => (
								<InternalLink href={PAGES.VIEW_ACTIVITY.path} pathParams={{ activityId }} groupName>
									{activityName}
								</InternalLink>
							),
						},
						{
							id: 'pipeline',
							header: 'Pipeline',
							cell: ({ pipelineName }) => (
								<InternalLink href={PAGES.VIEW_PIPELINE.path} pathParams={{ pipelineName }} groupName>
									{pipelineName}
								</InternalLink>
							),
						},
					]}
					items={searchResults}
					loading={loading}
					loadingText={`Searching activities in group ${groupName} by name...`}
					empty={`No activities found with name like "${searchString}" in group ${groupName}`}
				/>
			</Modal>
		</>
	);
}
